import { IActivity } from '../../data-models';
import {
  KnownDocIds,
  isFailedUpload,
  isTrackedUpload,
  isUploadAvailable,
  uploadAge,
  uploadCaseIdOf,
} from './rules';

/** Elapsed times since an upload's createdDate at which to check on it */
export const POLL_CHECKPOINTS_MS = [
  20, 40, 60, 120, 240, 300, 450, 600, 750, 900,
].map((seconds) => seconds * 1000);

const LAST_CHECKPOINT_MS = POLL_CHECKPOINTS_MS.at(-1);

// Timers fire late, so an upload still counts at its final checkpoint
const CHECKPOINT_GRACE_MS = 10 * 1000;

const isAwaitingDocument = (activity: IActivity, known: KnownDocIds) =>
  isTrackedUpload(activity) &&
  !isFailedUpload(activity) &&
  !isUploadAvailable(activity, known);

/**
 * @returns ms until the soonest checkpoint still ahead of any upload that is
 * awaiting its document, or undefined when polling can stop
 */
export const nextCheckpointDelay = (
  activities: IActivity[],
  now: number,
  known: KnownDocIds,
): number | undefined => {
  const delays = activities
    .filter((activity) => isAwaitingDocument(activity, known))
    .map((activity) => {
      const age = uploadAge(activity, now);
      const next = POLL_CHECKPOINTS_MS.find((checkpoint) => checkpoint > age);
      return next === undefined ? undefined : next - age;
    })
    .filter((delay): delay is number => delay !== undefined);

  return delays.length ? Math.min(...delays) : undefined;
};

/**
 * Groups the docIds of uploads CCM has accepted but that aren't available yet
 * by the case they were uploaded to
 * @returns A map of caseId to docIds
 */
export const groupPendingDocIdsByCase = (
  activities: IActivity[],
  now: number,
  known: KnownDocIds,
): Map<string, string[]> => {
  const docIdsByCase = new Map<string, string[]>();

  activities
    .filter(
      (activity) =>
        activity.docId != null &&
        isAwaitingDocument(activity, known) &&
        uploadAge(activity, now) <= LAST_CHECKPOINT_MS! + CHECKPOINT_GRACE_MS,
    )
    .forEach((activity) => {
      const caseId = uploadCaseIdOf(activity);
      const docIds = docIdsByCase.get(caseId) ?? [];
      docIdsByCase.set(caseId, [...docIds, String(activity.docId)]);
    });

  return docIdsByCase;
};

// Allows for the server's createdDate and the browser clock disagreeing
const UPLOAD_CLOCK_SKEW_MS = 2 * 60 * 1000;

/**
 * @returns true once activities include a tracked upload to the case created
 * no earlier than the upload completed (less clock skew)
 */
export const hasNewUpload = (
  activities: IActivity[],
  caseId: string,
  uploadedAt: number,
) =>
  activities.some(
    (activity) =>
      isTrackedUpload(activity) &&
      (activity.caseId === caseId || activity.uploadCaseId === caseId) &&
      new Date(activity.createdDate).getTime() >=
        uploadedAt - UPLOAD_CLOCK_SKEW_MS,
  );
